import React from 'react';
import { HoverableDIV } from '../../../../components/HoverableTD';
import { RiseOutlined, FallOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import {
  Bar,
  Cell,
  CartesianGrid,
  ComposedChart,
  BarChart,
  Label,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { getInDecimal } from '../../../../services/util';
import { formatDecimal } from '../../../../util';
import { findIndex, replace, split, toNumber, floor } from 'lodash';
import { regex } from '../../../../components/tm/CustomerRiskIndicator/riskIndicatorUtil';

const Container = styled.div`
  background: #f9f9f9;
  border: 1px solid #d9d9d9;
  flex: 1;
  min-height: 303px;
  display: flex;
  justify-content: space-evenly;
`;

const EqualDiv = styled.div`
  flex: 1;
  border-right: 1px solid #d9d9d9;
  padding: 16px 20px;
`;

const GraphIndicatorContainer = styled.div`
  padding-left: 10px;
  padding-right: 20px;
  border: solid 1px #b5c2cf;
  border-radius: 20px;
  display: flex;
`;

const CustomerRiskHead = styled.div`
  display: flex;
  margin-bottom: 10px;
  span {
    font-weight: 500;
    font-size: 14px;
    align-self: center;
    color: #455058;
  }
`;

const CustomTooltip = styled.div`
  background: #f2f4f6;
  padding: 8px 18px;
  border: 0.2px solid;
  min-width: 260px;
  box-shadow: 0 1px 13px 0 rgba(192, 103, 103, 0.5);
  border-radius: 8px;
`;

const PeerComparisonToolTip = (props) => {
  const { active, payload } = props;
  let { label } = props || {};
  const barPayload = payload[0]?.payload ? payload[0].payload : null;
  try {
    const tags = label.match(regex);
    if (tags.length > 2) {
      const val1 = Number(tags[1]).toFixed(2);
      const val2 = Number(tags[2]).toFixed(2);
      label = `(${val1} , ${val2})`;
    }
  } catch (e) {
    // do nothing
  }

  return active ? (
    <CustomTooltip>
      <div className="row">
        <div className="col-sm-12">
          <p>
            <b>Range:</b> {label}
          </p>
          <p>
            <b>Distribution:</b> {Number(barPayload.value).toFixed(2)}
          </p>
        </div>
      </div>
    </CustomTooltip>
  ) : null;
};

const GraphToolTip = ({ txt }) => {
  return (
    <div>
      <div style={{ color: 'gray' }}>Description</div>
      <br />
      {txt}
    </div>
  );
};

const DeviationBadge = ({ deviation, description, title }) => {
  return (
    <div className="deviation" id="pdValue">
      <HoverableDIV firstDataPoint={<GraphToolTip txt={description} />}>
        <GraphIndicatorContainer title={title}>
          {getInDecimal(deviation) >= 0 ? (
            <RiseOutlined style={{ color: '#E85252' }} />
          ) : (
            <FallOutlined style={{ color: '#0b8235' }} />
          )}
          <label
            style={{
              fontWeight: 500,
              margin: '0 0 0 5px',
              padding: 0
            }}>
            {getInDecimal(deviation, 5)}
          </label>
        </GraphIndicatorContainer>
      </HoverableDIV>
    </div>
  );
};

export const ProfileAndPeerGraph = ({
  peerProfileGraph = {},
  comparisonXAxisName,
  comparisonYAxisName,
  deviationXAxisName,
  deviationYAxisName
}) => {
  const getPeerIndex = (val) => {
    let ar = peerProfileGraph?.peerComparisonGraph.distribution;
    let index = findIndex(ar, (obj) => {
      let range = replace(obj.range, '(', '');
      range = replace(range, ')', '');
      let ranges = split(range, ',');

      if (val >= parseFloat(ranges[0]) && val <= parseFloat(ranges[1]))
        return obj;
    });
    return index;
  };

  const renderProfileDeviation = () => {
    const { profileDeviationGraph = '', peerComparisonGraph } =
      peerProfileGraph;
    if (!profileDeviationGraph) {
      return;
    } else {
      let profileDeviationData = [];
      if (profileDeviationGraph.distribution) {
        profileDeviationData = profileDeviationGraph.distribution.map((pdg) => {
          const value = parseFloat(pdg?.value);
          return {
            ...pdg,
            value
          };
        });
      }
      profileDeviationData = profileDeviationData.reverse();
      profileDeviationData.push({
        range: 'T0 (Current)',
        value: peerComparisonGraph.riValue
      });

      return (
        <EqualDiv>
          <CustomerRiskHead>
            <span>Profile Deviation</span>&nbsp;&nbsp;
            <DeviationBadge
              deviation={profileDeviationGraph?.deviation}
              title="Profile deviation"
              description={
                'The historical variation (trend over the time) is reflected by  upward or downward trend of profile deviation value. The higher the value the riskier profile'
              }
            />
          </CustomerRiskHead>
          <div style={{ width: '100%', height: 290 }}>
            {profileDeviationGraph.distribution && (
              <ResponsiveContainer height={310}>
                <ComposedChart
                  data={profileDeviationData}
                  margin={{
                    top: 45,
                    right: 65,
                    left: 20,
                    bottom: 20
                  }}>
                  <CartesianGrid stroke="#f5f5f5" />
                  <XAxis dataKey="range">
                    <Label
                      offset={-2}
                      value={deviationXAxisName}
                      padding={{ left: 20, right: 20 }}
                      position="insideBottom"
                      style={{
                        textAnchor: 'middle',
                        fill: '#6A7088',
                        font: '500 12px Inter'
                      }}
                    />
                  </XAxis>
                  <YAxis
                    tickSize={10}
                    allowDecimals={true}
                    interval={0}
                    type="number"
                    domain={[0, 'auto']}>
                    <Label
                      offset={-10}
                      angle={-90}
                      value={deviationYAxisName}
                      position="insideLeft"
                      style={{
                        textAnchor: 'middle',
                        fill: '#6A7088',
                        font: '500 10px Inter'
                      }}
                    />
                  </YAxis>
                  <Tooltip />
                  <Bar dataKey="value" barSize={35} fill="#F6D9AF" />
                  <Line
                    type="monotone"
                    strokeWidth="3"
                    dataKey="value"
                    stroke="#BC9A68"
                    dot={true}
                  />
                  {profileDeviationData.map((ele, ind) => {
                    if (ele.range === 'T0 (Current)') {
                      return (
                        <ReferenceDot
                          x={ele.range}
                          y={ele.value}
                          r={5}
                          fill="red"
                          stroke="none"
                          key={ind}>
                          <Label
                            value={`Val: ${formatDecimal(ele.value, 2)}`}
                            offset={5}
                            position="top"
                            style={{ fontSize: '10px', fill: '#335777' }}
                          />
                        </ReferenceDot>
                      );
                    } else
                      return (
                        <ReferenceDot
                          x={ele.range}
                          y={ele.value}
                          r={5}
                          fill="#335777"
                          stroke="none">
                          <Label
                            value={getInDecimal(ele.value, 5)}
                            offset={5}
                            position="top"
                            style={{ fontSize: '10px', fill: '#335777' }}
                          />
                        </ReferenceDot>
                      );
                  })}
                  <ReferenceLine
                    y={profileDeviationGraph.mean}
                    stroke="#6A839A"
                    strokeWidth={2}>
                    <Label
                      offset={22}
                      value={'Mean ' + profileDeviationGraph.mean}
                      position="top"
                      fill="black"
                      fontSize="10"
                    />
                  </ReferenceLine>
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </EqualDiv>
      );
    }
  };

  const renderPeerComparision = () => {
    const { peerComparisonGraph = '' } = peerProfileGraph;
    if (!peerComparisonGraph) {
      return;
    } else {
      let peerComparisionData = [];
      if (peerComparisonGraph.distribution) {
        peerComparisionData = peerComparisonGraph.distribution.map((pdg) => {
          const ranges = split(replace(pdg.range, /[()]/g, ''), ',');
          const range = `(${floor(toNumber(ranges[0]), 2)},${floor(
            toNumber(ranges[1]),
            2
          )})`;
          const value = parseFloat(pdg?.value);

          return {
            ...pdg,
            range,
            value
          };
        });
      }

      return (
        <EqualDiv style={{ borderRight: 'none' }}>
          <CustomerRiskHead>
            <span>Peer Comparision</span>&nbsp;&nbsp;
            <DeviationBadge
              deviation={peerComparisonGraph?.deviation}
              title="Peer Comparision"
              description={
                "Ranking of the customer's risk indicator value within its cluster."
              }
            />
          </CustomerRiskHead>
          <div style={{ width: '100%', height: 290 }}>
            {peerComparisonGraph.distribution && (
              <ResponsiveContainer height={310}>
                <ComposedChart
                  data={peerComparisionData}
                  margin={{
                    top: 45,
                    right: 0,
                    bottom: 20,
                    left: 5
                  }}>
                  <CartesianGrid stroke="#f5f5f5" />
                  <XAxis
                    dataKey="range"
                    type={'category'}
                    scale="point"
                    interval={8}
                    angle={0}
                    dx={20}
                    padding={{ left: 20, right: 20 }}>
                    <Label
                      offset={-2}
                      value={comparisonXAxisName}
                      position="insideBottom"
                      style={{
                        textAnchor: 'middle',
                        fill: '#6A7088',
                        font: '500 12px Inter',
                        display: 'hidden'
                      }}
                    />
                  </XAxis>
                  <YAxis
                    tickSize={10}
                    allowDecimals={true}
                    interval={0}
                    type="number"
                    domain={[0, 'auto']}>
                    <Label
                      offset={-8}
                      angle={-90}
                      value={comparisonYAxisName}
                      position="left"
                      style={{
                        textAnchor: 'middle',
                        fill: '#6A7088',
                        font: '500 10px Inter'
                      }}
                    />
                  </YAxis>
                  <Tooltip content={<PeerComparisonToolTip />} />
                  <Bar dataKey="value" barSize={10} fill="#00326840">
                    {peerComparisionData.map((ele, ind) => {
                      if (ind === getPeerIndex(peerComparisonGraph.riValue)) {
                        return <Cell fill={'#E85252'} key={ind} />;
                      } else {
                        return <Cell key={ind} />;
                      }
                    })}
                  </Bar>
                  <ReferenceLine
                    x={
                      peerComparisionData[
                        getPeerIndex(peerComparisonGraph.mean)
                      ]?.range
                    }
                    stroke="#335777">
                    <Label
                      value={'Mean'}
                      position="insideTop"
                      offset={0}
                      content={(props) => {
                        return (
                          <text
                            style={{ fontSize: '11px' }}
                            x={props.viewBox.x + 45}
                            y={10}
                            textAnchor="end"
                            fill="#335777">
                            {props.value} {peerComparisonGraph.mean}
                          </text>
                        );
                      }}
                    />
                  </ReferenceLine>
                  <ReferenceDot
                    alwaysShow={true}
                    isFront={true}
                    x={
                      peerComparisionData[
                        getPeerIndex(peerComparisonGraph.riValue)
                      ]?.range
                    }
                    y={
                      peerComparisionData[
                        getPeerIndex(peerComparisonGraph.riValue)
                      ]?.value
                    }
                    r={2.5}
                    fill="#382E62"
                    stroke="none">
                    <Label
                      dx={
                        getPeerIndex(peerComparisonGraph.riValue) ===
                        peerComparisionData.length - 1
                          ? -40
                          : 0
                      }
                      value={`Val: ${formatDecimal(
                        peerComparisonGraph?.riValue
                          ? peerComparisonGraph?.riValue
                          : 0,
                        2
                      )} (Percentile: ${formatDecimal(
                        peerComparisonGraph?.percentile
                      )}%)`}
                      offset={4}
                      position="top"
                      style={{
                        fontSize: '10px',
                        fill: '#6A7088',
                        fontWeight: '500'
                      }}
                    />
                  </ReferenceDot>
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </EqualDiv>
      );
    }
  };

  return (
    <Container>
      {renderProfileDeviation()}
      {renderPeerComparision()}
    </Container>
  );
};

export default ProfileAndPeerGraph;
